import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, Plus } from "lucide-react";

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallBanner() {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem("jampa-install-dismissed")) return;

    const handler = (e) => {
      e.preventDefault();
      setPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    let t;
    if (isMobile) {
      // Android may fire beforeinstallprompt; iOS never does — show banner anyway
      t = setTimeout(() => setVisible(true), 2000);
    }
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const install = async () => {
    if (prompt) {
      prompt.prompt();
      try {
        await prompt.userChoice;
      } catch (e) { console.debug("PWA install prompt dismissed:", e); }
      setPrompt(null);
      dismiss();
      return;
    }
    if (isIOS()) {
      setShowIOS(true);
      return;
    }
    dismiss();
  };

  const dismiss = () => {
    setVisible(false);
    setShowIOS(false);
    localStorage.setItem("jampa-install-dismissed", "1");
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <div className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-[70] w-[92%] max-w-md">
          <motion.div
            data-testid="pwa-install-banner"
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
          >
            <div className="relative glass rounded-3xl border border-border bg-card/95 p-4 pr-10 soft-shadow flex items-center gap-3">
              <button
                data-testid="pwa-dismiss-button"
                onClick={dismiss}
                aria-label="Fechar"
                className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground active:scale-90 transition-transform"
              >
                <X className="w-4 h-4" />
              </button>
              <img src="/logo.png" alt="Guia de Jampa AI" className="w-12 h-12 rounded-2xl object-contain bg-white" />
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm leading-tight">Adicionar à tela inicial</p>
                <p className="text-xs text-muted-foreground">Instale o Guia de Jampa AI como app.</p>
              </div>
              <button
                data-testid="pwa-install-button"
                onClick={install}
                className="flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold active:scale-95 transition-transform shrink-0"
              >
                <Download className="w-4 h-4" /> Instalar
              </button>
            </div>
          </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* iOS instructions sheet */}
      <AnimatePresence>
        {showIOS && (
          <motion.div
            data-testid="pwa-ios-sheet"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 backdrop-blur-sm"
            onClick={dismiss}
          >
            <motion.div
              initial={{ y: 300 }}
              animate={{ y: 0 }}
              exit={{ y: 300 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-card rounded-t-3xl p-6 pb-10 border-t border-border"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <img src="/logo.png" alt="Guia de Jampa AI" className="w-12 h-12 rounded-2xl object-contain bg-white" />
                  <div>
                    <p className="font-heading font-bold">Instalar no iPhone</p>
                    <p className="text-xs text-muted-foreground">Adicione à Tela de Início</p>
                  </div>
                </div>
                <button data-testid="pwa-ios-close" onClick={dismiss} aria-label="Fechar" className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <ol className="space-y-4">
                <li className="flex items-center gap-3 text-sm">
                  <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs shrink-0">1</span>
                  <span>Toque no botão <b>Compartilhar</b></span>
                  <Share className="w-5 h-5 text-primary ml-auto" />
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs shrink-0">2</span>
                  <span>Selecione <b>Adicionar à Tela de Início</b></span>
                  <Plus className="w-5 h-5 text-primary ml-auto" />
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs shrink-0">3</span>
                  <span>Toque em <b>Adicionar</b> no canto superior</span>
                </li>
              </ol>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
